import React, { useState } from 'react';
import { createStone, completeStoneUpload } from '../../utils/adminApi';
import { uploadFilesToS3 } from '../../utils/uploadToS3';
import './CreateStoneTab.css';

const CreateStoneTab = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    accessLevel: 'friends',
    placedBy: 'Jeff Marshall',
    placingMemoryText: ''
  });
  const [mediaFiles, setMediaFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadsInProgress, setUploadsInProgress] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Clean up object URLs when component unmounts
  React.useEffect(() => {
    return () => {
      mediaFiles.forEach(file => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
    };
  }, [mediaFiles]);

  // Warn before closing if uploads in progress
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploadsInProgress) {
        e.preventDefault();
        e.returnValue = 'Media files are still uploading. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploadsInProgress]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types first
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/heic', 'image/webp'];
    const allowedVideoTypes = ['video/mp4', 'video/quicktime', 'video/mov', 'video/avi', 'video/x-m4v'];
    const allowedTypes = [...allowedImageTypes, ...allowedVideoTypes];
    
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type.toLowerCase()));
    
    if (invalidFiles.length > 0) {
        setError(`Invalid file type: ${invalidFiles[0].name}. Please use JPG, PNG, GIF, HEIC, WEBP for photos or MP4, MOV for videos.`);
        e.target.value = '';
        return;
    }
    
    // Validate file count
    const currentPhotoCount = mediaFiles.filter(f => f.type === 'image').length;
    const currentVideoCount = mediaFiles.filter(f => f.type === 'video').length;
    const newPhotos = files.filter(f => f.type.startsWith('image/'));
    const newVideos = files.filter(f => f.type.startsWith('video/'));
    
    if (currentPhotoCount + newPhotos.length > 20) {
        setError('Maximum 20 photos');
        e.target.value = '';
        return;
    }
    
    if (currentVideoCount + newVideos.length > 5) {
        setError('Maximum 5 videos');
        e.target.value = '';
        return;
    }
    
    // Validate file sizes
    for (const file of files) {
        if (file.size > 150 * 1024 * 1024) { // 150MB
        setError(`${file.name} is too large (max 150MB)`);
        e.target.value = '';
        return;
        }
    }
    
    setError('');

    // Create local previews only (no upload yet)
    const newItems = files.map(file => ({
        type: file.type.startsWith('image/') ? 'image' : 'video',
        file,
        fileName: file.name,
        previewUrl: URL.createObjectURL(file),
        s3Key: null
    }));
    
    setMediaFiles(prev => [...prev, ...newItems]);  
  };

  const removeFile = (index) => {
    const toRemove = mediaFiles[index];
    if (toRemove?.previewUrl) {
      URL.revokeObjectURL(toRemove.previewUrl);
    }
    setMediaFiles(mediaFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSubmitting(true);
    
    try {
      // Validate required fields
      if (!formData.name || !formData.latitude || !formData.longitude) {
        throw new Error('Name, latitude, and longitude are required');
      }
      
      // Phase 1: Create stone and get presigned URLs
      const mediaFilesMetadata = mediaFiles.map(item => ({
        fileName: item.file.name,
        fileType: item.file.type,
        fileSize: item.file.size
      }));

      const response = await createStone({
        ...formData,
        mediaFilesMetadata: mediaFilesMetadata.length > 0 ? mediaFilesMetadata : undefined
      });

      const { stoneId, placingVisitId, presignedUrls } = response;
      
      // Show success immediately!
      setSuccess(true);
      setSubmitting(false);
      
      // Clean up preview URLs
      mediaFiles.forEach(file => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        latitude: '',
        longitude: '',
        accessLevel: 'friends',
        placedBy: 'Jeff Marshall',
        placingMemoryText: ''
      });
      setMediaFiles([]);
      
      // Phase 2: Upload files in background (silently)
      if (presignedUrls && presignedUrls.length > 0) {
        uploadInBackground(stoneId, placingVisitId, mediaFiles.map(m => m.file), presignedUrls);
      }
      
    } catch (err) {
      setError(err.message || 'Failed to create stone');
      console.error(err);
      setSubmitting(false);
    }
  };

  // Background upload function (fire and forget)
  const uploadInBackground = async (stoneId, placingVisitId, files, presignedUrls) => {
    try {
      setUploadsInProgress(true);
      console.log('Starting background upload for stone:', stoneId);
      
      // Upload files to S3
      const uploadedFiles = await uploadFilesToS3(files, presignedUrls);
      
      console.log('Files uploaded, calling completion API...');
      
      // Call completion Lambda
      await completeStoneUpload({
        stoneId,
        placingVisitId,
        mediaFiles: uploadedFiles
      });
      
      console.log('Stone upload completed successfully');
      
    } catch (error) {
      console.error('Background upload failed:', error);
      // Don't show error to user - they already got success message
      // Stone exists, just without media. Admin can see uploadStatus in future.
    } finally {
      setUploadsInProgress(false);
    }
  };

  return (
    <div className="create-stone-tab">
      <h2>Add New Stone</h2>
      
      {success && (
        <div className="success-banner">
          Stone created successfully! 
          {uploadsInProgress && <span> 📤 Media uploading in background...</span>}
          <button 
            onClick={() => setSuccess(false)} 
            style={{ marginLeft: '10px', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="stone-form">
        <div className="form-row">
          <div className="form-field">
            <label>Stone Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Golden Gate Park"
              required
              disabled={submitting}
            />
          </div>
          
          <div className="form-field">
            <label>Placed By</label>
            <input
              type="text"
              name="placedBy"
              value={formData.placedBy}
              onChange={handleChange}
              disabled={submitting}
            />
          </div>
        </div>
        
        <div className="form-field">
          <label>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Story or significance of this location..."
            rows="4"
            disabled={submitting}
          />
        </div>
        
        <div className="form-row">
          <div className="form-field">
            <label>Latitude *</label>
            <input
              type="number"
              step="any"
              name="latitude"
              value={formData.latitude}
              onChange={handleChange}
              placeholder="37.7749"
              required
              disabled={submitting}
            />
          </div>
          
          <div className="form-field">
            <label>Longitude *</label>
            <input
              type="number"
              step="any"
              name="longitude"
              value={formData.longitude}
              onChange={handleChange}
              placeholder="-122.4194"
              required
              disabled={submitting}
            />
          </div>
        </div>
        
        <div className="form-field">
          <label>Access Level</label>
          <select
            name="accessLevel"
            value={formData.accessLevel}
            onChange={handleChange}
            disabled={submitting}
          >
            <option value="friends">Friends (all authenticated users)</option>
            <option value="family">Family Only</option>
          </select>
        </div>
        
        <div className="form-field">
          <label>Initial Placing Memory (Optional)</label>
          <textarea
            name="placingMemoryText"
            value={formData.placingMemoryText}
            onChange={handleChange}
            placeholder="Thoughts or story about placing this stone..."
            rows="4"
            disabled={submitting}
          />
        </div>
        
        <div className="form-field">
          <label>Placing Photos/Videos (Optional)</label>
          <p className="field-hint">Up to 20 photos and 5 videos, max 150MB each</p>
          
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileSelect}
            disabled={submitting}
            style={{ display: 'none' }}
            id="stone-file-input"
          />
          
          <label htmlFor="stone-file-input" className="file-upload-btn">
            Choose Photos/Videos
          </label>

          {error && (
            <div className="upload-error-message">
            {error}
            </div>
          )}
          
          {mediaFiles.length > 0 && (
            <div className="media-preview-grid">
              {mediaFiles.map((file, index) => (
                <div key={index} className="media-preview-item">
                  {file.type === 'image' && (
                    <img src={file.previewUrl} alt={file.fileName} />
                  )}
                  {file.type === 'video' && (
                    <div className="video-preview">
                      <video src={file.previewUrl} />
                      <div className="play-icon">▶</div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="remove-file-btn"
                    onClick={() => removeFile(index)}
                    disabled={submitting}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {error && (
          <div className="error-message" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}
        
        <button
          type="submit"
          className="submit-stone-btn"
          disabled={submitting}
        >
          {submitting ? 'Creating Stone...' : 'Create Stone'}
        </button>
      </form>
    </div>
  );
};

export default CreateStoneTab;